import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Fade,
  Avatar,
  Tooltip,
  Snackbar,
  IconButton,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAllNotification } from '../Query/NotificationQuery';
import { useAllMarkRead } from '../Query/MarkReadQuery';
import {COLORS} from "../../theme/themeConstants"
interface NotificationPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const NotificationPopover: React.FC<NotificationPopoverProps> = ({ anchorEl, open, onClose }) => {
  const [page, setPage] = useState(1);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const params = useMemo(() => {
    let userId = '';
    let userType = '';
    try {
      const storedUser = localStorage.getItem('userData');
      const parsed = storedUser ? JSON.parse(storedUser) : null;
      userId = parsed?.userId || '';
      const rawUserType = parsed?.userType || '';
      userType = (rawUserType === 'superAdmin' || rawUserType === 'subadmin') ? 'admin' : rawUserType;
    } catch (error) {
      console.error('Failed to parse user data:', error);
    }
    return {
      userId,
      userType,
      page,
      limit: 10,
      refreshKey
    };
  }, [page, refreshKey]);

  const { data: notificationData, isLoading: notificationLoading } = useAllNotification(params);
  const { mutate: markAllAsRead } = useAllMarkRead();

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleRefresh = () => {
    setSnackbarMessage('Notifications refreshed');
    setShowSnackbar(true);
    setNotifications([]); // Clear existing notifications
    setPage(1); // Reset to first page
    setHasMore(true); // Reset pagination state
    setRefreshKey(prev => prev + 1); // Force cache invalidation
  };

  useEffect(() => {
    if (!open) return;
    if (!notificationData) return;
    const newData = notificationData.notifications || [];
    setNotifications(page === 1 ? newData : [...notifications, ...newData]);
    setHasMore(notificationData.page * notificationData.limit < notificationData.totalRecords);
  }, [page, open, notificationData]);

  useEffect(() => {
    if (!open) {
      setNotifications([]);
      setPage(1);
      setHasMore(true);
    }
  }, [open]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastNotificationRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (notificationLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore && !notificationLoading) {
          setPage(prev => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [notificationLoading, hasMore]
  );

  useEffect(() => {
    if (open) {
      let userId = '';
      let userType = '';
      try {
        const storedUser = localStorage.getItem('userData');
        const parsed = storedUser ? JSON.parse(storedUser) : null;
        userId = parsed?.userId || '';
        const rawUserType = parsed?.userType || '';
        userType = (rawUserType === 'superAdmin' || rawUserType === 'subadmin') ? 'admin' : rawUserType;
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
      markAllAsRead({ userId, userType, isRead: true });
    }
  }, [open, markAllAsRead]);

  // Allow background page to remain scrollable while popover is open
  // Removed global body overflow manipulation to avoid locking background scroll

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 650,
            borderRadius: 2,
            p: 0,
            // Prevent Popover paper from creating its own scrollbar
            overflow: 'visible'
          }
        }}
      >
        <Box
          sx={{
            maxHeight: 800,
            overflowY: 'auto',
            overflowX: 'hidden',
            background: '#ffffff',
            padding: 0
          }}
        >
          <Box
            sx={{
              background: COLORS.primary.main,
              color: COLORS.secondary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid #ececec',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <NotificationsIcon sx={{ fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18 }}>
                Notifications
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  sx={{ color: 'white' }}
                  disabled={notificationLoading}
                  size="small"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                onClick={onClose}
                sx={{ color: 'white' }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {notifications.length === 0 && !notificationLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                textAlign: 'center'
              }}
            >
              <NotificationsIcon sx={{ fontSize: 64, color: COLORS.secondary.main, marginBottom: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No notifications
              </Typography>
              <Typography variant="body2" color="textSecondary">
                You're all caught up!
              </Typography>
            </Box>
          ) : (
            <List sx={{ padding: 0 }}>
              {notifications.map((notification, index) => {
                // Parse notificationData to extract orderId if it exists
                let orderId = null;
                try {
                  if (notification.notificationData) {
                    const parsedData = JSON.parse(notification.notificationData);
                    orderId = parsedData.orderId || null;
                  }
                } catch (error) {
                  console.error('Failed to parse notificationData:', error);
                }

                const listItemContent = (
                  <ListItem
                    component="div"
                    sx={{
                      padding: '16px 20px',
                      borderBottom: '1px solid rgba(0,0,0,0.06)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(103, 126, 234, 0.1)',
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: COLORS.primary.main,
                          color: COLORS.secondary.main
                        }}
                        src={notification.notificationImage}
                      >
                        {/* <NotificationsIcon /> */}
                        <NotificationsActiveIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {notification.title}
                          </Typography>
                          {orderId && (
                            <Typography
                              variant="body2"
                              color="#a84d6e"
                              sx={{
                                fontWeight: 600,
                                marginTop: 0.5,
                                fontSize: '0.85rem'
                              }}
                            >
                              Order ID: {orderId}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.4,
                              marginBottom: 1,
                              fontWeight: 500,
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere'
                            }}
                          >
                            {notification.body}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={notification.notificationType}
                              size="small"
                              sx={{
                                backgroundColor: COLORS.primary.main,
                                color: COLORS.secondary.main,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                maxWidth: 120,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ marginLeft: 'auto' }}
                            >
                              {notification.createdAt}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                );

                return (
                  <Fade in={true} timeout={300 + index * 100} key={notification.id}>
                    {index === notifications.length - 1 ? (
                      <div ref={lastNotificationRef}>{listItemContent}</div>
                    ) : (
                      listItemContent
                    )}
                  </Fade>
                );
              })}
              {notificationLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </List>
          )}
        </Box>

        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
          message={snackbarMessage}
        />
      </Popover>
    </>
  );
};

export default NotificationPopover;
